import React, { useState, useRef, createContext, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Home from './pages/Home';
import PageVideo from './pages/PageVideo';
import Live from './pages/Live'; 
import Contact from './pages/Contact';
import Header from './components/Header';
import Footer from './components/Footer';
import AuthPage from './pages/AuthPage.js';

// --- CONTEXTE GLOBAL (AUDIO + UTILISATEUR) ---
export const AudioContext = createContext();

const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isAdminPage = location.pathname === '/login' || location.pathname === '/dashboard';
  return (
    <>
      {!isAdminPage && <Header />}
      {children}
      {!isAdminPage && <Footer />}
    </>
  );
};

function App() {
  // --- ÉTATS ET RÉFÉRENCES ---
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const audioRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lastTimePaused, setLastTimePaused] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);

  // --- UTILITAIRE DE FORMATAGE AMÉLIORÉ ---
  const formatText = (str) => {
    if (!str) return "";
    return str.toLowerCase().replace(/(^|\s|-)\S/g, (match) => match.toUpperCase());
  };

  // --- LOGIQUE DU TITRE DYNAMIQUE ET BLUETOOTH ---
  const startMarquee = useCallback((text) => {
    if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
    
    // Ajout d'espaces avant et après les étoiles pour une démarcation nette
    let displayTitle = text + "          *** "; 
    
    scrollIntervalRef.current = setInterval(() => {
      displayTitle = displayTitle.substring(1) + displayTitle.substring(0, 1);
      document.title = displayTitle;
    }, 300);
  }, []);

  const fetchGlobalTrack = useCallback(async () => {
    try {
      const response = await fetch('https://api.radioking.io/widget/radio/frequence-9742/track/current');
      const data = await response.json();
      
      const title = formatText(data.title || "Titre inconnu");
      const artist = formatText(data.artist || "Fréquence 974");
      const cover = data.cover || "https://www.radioking.com/play/frequence-9742/cover";

      // 1. Mise à jour de l'onglet navigateur
      startMarquee(`FRÉQUENCE 974  |  ${artist}  |  ${title}`);

      // 2. Mise à jour Métadonnées Bluetooth / Mobile
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: title,
          artist: artist,
          album: 'Fréquence 974',
          artwork: [
            { src: cover, sizes: '96x96', type: 'image/png' },
            { src: cover, sizes: '512x512', type: 'image/png' }
          ]
        });
      }
    } catch (error) {
      console.error("Erreur titre global:", error);
      document.title = "Fréquence 974";
    }
  }, [startMarquee]);

  // --- ACTIONS AUDIO ---
  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const pauseAudio = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.volume = 1;
      setLastTimePaused(Date.now());
      setIsPlaying(false);
    }
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      let vol = 1.0;
      const fadeOut = setInterval(() => {
        if (vol > 0.1) {
          vol -= 0.1;
          audioRef.current.volume = vol;
        } else {
          clearInterval(fadeOut);
          pauseAudio();
          audioRef.current.volume = 1;
        }
      }, 100);
    } else {
      setActiveVideoId(null); 
      const now = Date.now();
      const diff = lastTimePaused ? (now - lastTimePaused) / 1000 : 999;
      if (diff > 60) audioRef.current.load();
      audioRef.current.volume = 0;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        let vol = 0;
        const fadeIn = setInterval(() => {
          if (vol < 0.9) {
            vol += 0.1;
            audioRef.current.volume = vol;
          } else {
            audioRef.current.volume = 1;
            clearInterval(fadeIn);
          }
        }, 100);
      }).catch(err => console.error(err));
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = ""; 
      audioRef.current.src = "https://www.radioking.com/play/frequence-9742";
      setIsPlaying(false);
      setLastTimePaused(null);
    }
  };

  // --- EFFECTS ---
  useEffect(() => {
    fetchGlobalTrack();
    const interval = setInterval(fetchGlobalTrack, 10000);
    return () => {
      clearInterval(interval);
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
    };
  }, [fetchGlobalTrack]);

  useEffect(() => {
    if (!isPlaying) return;
    const handleHtml5Play = (e) => e.target !== audioRef.current && pauseAudio();
    const handleIframeClick = () => {
      setTimeout(() => document.activeElement.tagName === 'IFRAME' && pauseAudio(), 100);
    };
    document.addEventListener('play', handleHtml5Play, true);
    window.addEventListener('blur', handleIframeClick);
    return () => {
      document.removeEventListener('play', handleHtml5Play, true);
      window.removeEventListener('blur', handleIframeClick);
    };
  }, [isPlaying, pauseAudio]);

  return (
    <AudioContext.Provider value={{ 
      user, 
      isPlaying, 
      togglePlay, 
      stopAudio, 
      pauseAudio, 
      activeVideoId, 
      setActiveVideoId 
    }}>
      <Router>
        <audio ref={audioRef} src="https://www.radioking.com/play/frequence-9742" preload="none"></audio>
        <LayoutWrapper>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pagevideo" element={<PageVideo />} />
            <Route path="/direct" element={<Live />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={user ? <Navigate to="/" /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/dashboard" element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
          </Routes>
        </LayoutWrapper>
      </Router>
    </AudioContext.Provider>
  );
}

export default App;